import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CarouselContext, ICarouselState } from './useCarousel';

interface ICarouselProviderProps {
  animationTime?: number;
  auto?: boolean;
}

const CarouselController: FC<React.PropsWithChildren<ICarouselProviderProps>> = ({
  animationTime = 0,
  auto = false,
  children,
}) => {
  // registering slides to understand what we need to render
  const [slides, setSlides] = useState<HTMLDivElement[]>([]);
  const addSlide = useCallback((slide: HTMLDivElement) => setSlides((s) => [...s, slide]), []);
  const removeSlide = useCallback(
    (slide: HTMLDivElement) => setSlides((slides) => slides.filter((s) => s !== slide)),
    []
  );
  // current slide
  const [current, setCurrent] = useState(0);
  // slider is running
  const [running, setRunning] = useState(auto);
  // timeout handle
  const animationHandle = useRef<number | NodeJS.Timeout>();

  const slideNext = useCallback(() => {
    const next = slides.length > 0 ? (current + 1) % slides.length : 0;
    setCurrent(next);
  }, [current, slides.length, setCurrent]);

  const stopSlider = useCallback(() => {
    if (!animationHandle.current) {
      // do not stop animation if already stopped
      return;
    }
    clearTimeout(animationHandle.current);
    animationHandle.current = undefined;
  }, []);

  const runSlider = useCallback(() => {
    if (animationHandle.current) {
      // do not start animation if already started
      return;
    }
    if (!animationTime) {
      // do not start animation if animation timeout value not provided or set to 0
      return;
    }
    animationHandle.current = setTimeout(slideNext, animationTime);
  }, [animationTime, slideNext]);

  useEffect(() => {
    if (!animationTime) {
      // do not start animation until timeout not set
      return;
    }
    // start animation if not paused
    if (!running) {
      return;
    }
    runSlider();
    // stop animation
    return stopSlider;
  }, [animationTime, running, runSlider, stopSlider]);

  const pauseSlider = useCallback(() => {
    setRunning(false);
    stopSlider();
  }, [setRunning, stopSlider]);

  const unpauseSlider = useCallback(() => {
    setRunning(true);
    runSlider();
  }, [setRunning, runSlider]);

  const state: ICarouselState = useMemo(
    () => ({
      current,
      length: slides.length,
      addSlide,
      removeSlide,
      pauseSlider,
      unpauseSlider,
    }),
    [current, slides.length, addSlide, removeSlide, pauseSlider, unpauseSlider]
  );
  return <CarouselContext.Provider value={state}>{children}</CarouselContext.Provider>;
};

export default CarouselController;
